import React, { ReactElement } from "react";
import { ConversationSource, ConversationSourceType, useGetConversationSourcesQuery } from "../../../__generated__/api";
import Select, { Option, OptionWithChildren, SelectInstanceHandler } from "../../select/Select";
import { Filters } from "../../filters/Filters";

export interface ConversationSourcesProps<TIsMulti = false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    shouldIncludeTags?: boolean;
    shouldIncludeEmail?: boolean;
    toggleText?: string;
    className?: string;
    shouldShowDropdown?: boolean;
    searchMinimum?: number;
    toggleClassName?: string;
    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (sourceId: (TIsMulti extends true ? string[] : string)) => void;
}

const SOURCE_TYPES = {
    [ConversationSourceType.ConversationGroup]: "Conversation Tag",
    [ConversationSourceType.SlackChannel]: "Channel",
    [ConversationSourceType.Email]: "Email",
};

const ConversationSources = React.forwardRef(<TIsMulti extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, shouldIncludeTags, shouldShowDropdown,
    toggleText, className, toggleClassName, searchMinimum, shouldIncludeEmail,
}: ConversationSourcesProps<TIsMulti>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useGetConversationSourcesQuery({
        variables: {
            shouldIncludeTags,
            shouldIncludeEmail,
        },
    });

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<ConversationSource>[] : Option<ConversationSource>;
    type ValueType = TIsMulti extends true ? Option<ConversationSource>[] : Option<ConversationSource>;

    const handleSelected = (selected: SelectedType) => {
        const values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<ConversationSource>).value) as Parameters<typeof onSelected>[0];
        onSelected(values);
    };

    const values: OptionWithChildren<ConversationSource>[] = Object.entries(data?.conversationSources?.groupBy("type") ?? [])
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .map(([__, children]) => ({
            label: SOURCE_TYPES[children.firstElement.type as ConversationSourceType],
            options: children.dedupe(row => row.id).map(row => ({
                label: row.label,
                value: row.id,
            })),
        }));

    const valuesFlat = values.flatMap(row => row.options);
    const valueMap = valuesFlat.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    if (shouldShowDropdown) {
        return (
            <Filters
                toggleText={toggleText}
                isMulti={isMulti}
                searchMinimum={searchMinimum}
                categories={values}
                className={className}
                toggleClassName={toggleClassName}
                selectedCategories={
                    (isMulti
                        ? new Set(!value || Array.isArray(value) ? value : [value])
                        : (value as string)) as TIsMulti extends true ? Set<string> : string
                }
                onSelected={current => (isMulti
                    ? (onSelected as ((value: string[]) => void))(Array.from(current))
                    : (onSelected as ((value: string) => void))(current as string))}
            />
        );
    }

    return (
        <Select
            ref={ref}
            minRequired={minRequired}
            placeholder={placeholder}
            isMulti={isMulti}
            onChange={handleSelected}
            options={shouldIncludeTags !== false ? values : data?.conversationSources?.map(row => ({
                label: row.label,
                value: row.id,
            })) ?? []}
            value={selected as ValueType}
        />
    );
});

ConversationSources.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    shouldIncludeTags: undefined,
    toggleText: undefined,
    className: undefined,
    shouldShowDropdown: undefined,
    toggleClassName: undefined,
    searchMinimum: undefined,
    shouldIncludeEmail: false,
};

export default ConversationSources as <
    TMulti extends boolean = false
> (
    props: ConversationSourcesProps<TMulti> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof ConversationSources>;
